import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { DateRange } from 'react-date-range';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement,
} from 'chart.js';
import { API_BASE, apiFetch } from '../lib/api';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement
);

type Persona = string;

type Metric = {
    label: string;
    value: number | string;
    helper?: string;
};

type AnalyticsChart = {
    id: string;
    title: string;
    type: 'bar' | 'line' | 'doughnut';
    labels: string[];
    datasets: Array<{
        label: string;
        data: number[];
    }>;
};

type AnalyticsTable = {
    title: string;
    columns: string[];
    rows: Array<Record<string, string | number | null>>;
    pagination: {
        page: number;
        pageSize: number;
        totalRows: number;
        totalPages: number;
    };
};

type AnalyticsPayload = {
    persona: Persona;
    scope: {
        startDate: string;
        endDate: string;
    };
    analytics: {
        metrics: Metric[];
        charts: AnalyticsChart[];
        table?: AnalyticsTable;
    };
};



const palette = [
    'rgba(37, 99, 235, 0.72)',
    'rgba(5, 150, 105, 0.72)',
    'rgba(217, 119, 6, 0.72)',
    'rgba(220, 38, 38, 0.72)',
    'rgba(124, 58, 237, 0.72)',
    'rgba(8, 145, 178, 0.72)',
];

const borderPalette = [
    'rgb(37, 99, 235)',
    'rgb(5, 150, 105)',
    'rgb(217, 119, 6)',
    'rgb(220, 38, 38)',
    'rgb(124, 58, 237)',
    'rgb(8, 145, 178)',
];

const toIsoDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
    const end = new Date();
    const start = new Date(end);
    start.setFullYear(end.getFullYear() - 1);
    start.setDate(start.getDate() + 1);

    return {
        startDate: start,
        endDate: end,
        key: 'selection',
    };
};

const today = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};

const quickRanges = [
    {
        label: 'Today',
        range: () => ({ startDate: today(), endDate: today() }),
    },
    {
        label: 'Yesterday',
        range: () => {
            const d = today();
            d.setDate(d.getDate() - 1);
            return { startDate: d, endDate: d };
        },
    },
    {
        label: 'Last 7 Days',
        range: () => {
            const end = today();
            const start = new Date(end);
            start.setDate(end.getDate() - 6);
            return { startDate: start, endDate: end };
        },
    },
    {
        label: 'Last 30 Days',
        range: () => {
            const end = today();
            const start = new Date(end);
            start.setDate(end.getDate() - 29);
            return { startDate: start, endDate: end };
        },
    },
    {
        label: 'Last 1 Year',
        range: () => {
            const end = today();
            const start = new Date(end);
            start.setFullYear(end.getFullYear() - 1);
            start.setDate(start.getDate() + 1);
            return { startDate: start, endDate: end };
        },
    },
] as const;

const isQuickRangeActive = (
    range: { startDate: Date; endDate: Date },
    selected: { startDate: Date; endDate: Date }
) => {
    return (
        toIsoDate(range.startDate) === toIsoDate(selected.startDate) &&
        toIsoDate(range.endDate) === toIsoDate(selected.endDate)
    );
};

const formatDisplayDate = (date: Date) => date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
});

const formatMetricValue = (metric: Metric) => {
    if (typeof metric.value === 'number') {
        return Number.isInteger(metric.value) ? metric.value.toLocaleString('en-IN') : metric.value.toFixed(1);
    }

    const parsed = Number(metric.value);
    if (Number.isFinite(parsed)) {
        return Number.isInteger(parsed) ? parsed.toLocaleString('en-IN') : parsed.toFixed(1);
    }

    return metric.value;
};

const prettifyColumn = (column: string) => {
    return column
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

const withChartStyle = (chart: AnalyticsChart) => ({
    labels: chart.labels,
    datasets: chart.datasets.map((dataset, index) => ({
        ...dataset,
        backgroundColor: chart.type === 'doughnut'
            ? chart.labels.map((_label, labelIndex) => palette[labelIndex % palette.length])
            : palette[index % palette.length],
        borderColor: chart.type === 'doughnut'
            ? chart.labels.map((_label, labelIndex) => borderPalette[labelIndex % borderPalette.length])
            : borderPalette[index % borderPalette.length],
        borderWidth: chart.type === 'line' ? 2 : 1,
        tension: chart.type === 'line' ? 0.3 : undefined,
        fill: chart.type === 'line' ? false : undefined,
    })),
});

const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: 'bottom' as const,
        },
    },
};

const renderChart = (chart: AnalyticsChart) => {
    const data = withChartStyle(chart);

    if (chart.type === 'line') {
        return <Line data={data} options={chartOptions} />;
    }

    if (chart.type === 'doughnut') {
        return <Doughnut data={data} options={chartOptions} />;
    }

    return <Bar data={data} options={chartOptions} />;
};

export default function AnalyticsPage() {
    const [persona, setPersona] = useState<Persona>('Police Station Officer');
    const [personas, setPersonas] = useState<Persona[]>([]);
    const [selectedRange, setSelectedRange] = useState<ReturnType<typeof getDefaultDateRange> | null>(getDefaultDateRange);
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [payload, setPayload] = useState<AnalyticsPayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [tablePage, setTablePage] = useState(1);
    const [tablePageSize, setTablePageSize] = useState(10);
    const [tableLoading, setTableLoading] = useState(false);
    const datePickerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const fetchPersonas = async () => {
            try {
                const res = await apiFetch(`${API_BASE}/api/constants/personas`);
                if (res.ok) {
                    const data = await res.json();
                    setPersonas(data.personas || []);
                    if (data.personas?.length > 0 && !data.personas.includes(persona)) {
                        setPersona(data.personas[0]);
                    }
                }
            } catch (err) {
                console.error('Failed to fetch personas', err);
            }
        };
        fetchPersonas();
    }, []);

    const scope = useMemo(() => selectedRange ? ({
        startDate: toIsoDate(selectedRange.startDate),
        endDate: toIsoDate(selectedRange.endDate),
    }) : null, [selectedRange]);

    useEffect(() => {
        if (!datePickerOpen) return;

        const handleClick = (event: MouseEvent) => {
            if (datePickerRef.current?.contains(event.target as Node)) return;
            setDatePickerOpen(false);
        };

        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [datePickerOpen]);

    useEffect(() => {
        const fetchAnalytics = async () => {
            setLoading(true);
            setError('');

            const params = new URLSearchParams({ persona });
            if (scope) {
                params.set('startDate', scope.startDate);
                params.set('endDate', scope.endDate);
            }
            params.set('page', '1');
            params.set('pageSize', String(tablePageSize));

            try {
                const response = await apiFetch(`${API_BASE}/api/analytics?${params.toString()}`);
                const data = await response.json().catch(() => ({}));

                if (!response.ok || !data.ok) {
                    setPayload(null);
                    setError(data?.message ?? 'Failed to load analytics');
                    return;
                }

                setPayload(data as AnalyticsPayload);
                setTablePage(1);
            } catch {
                setPayload(null);
                setError('Failed to load analytics');
            } finally {
                setLoading(false);
            }
        };

        fetchAnalytics();
    }, [persona, scope?.startDate, scope?.endDate, tablePageSize]);

    /* Fetch only table data when page changes (skip full reload) */
    useEffect(() => {
        if (!payload || tablePage === 1) return; // page 1 is handled by the main fetch

        const fetchTablePage = async () => {
            setTableLoading(true);

            const params = new URLSearchParams({ persona });
            if (scope) {
                params.set('startDate', scope.startDate);
                params.set('endDate', scope.endDate);
            }
            params.set('page', String(tablePage));
            params.set('pageSize', String(tablePageSize));

            try {
                const response = await apiFetch(`${API_BASE}/api/analytics?${params.toString()}`);
                const data = await response.json().catch(() => ({}));

                if (response.ok && data.ok) {
                    setPayload(data as AnalyticsPayload);
                }
            } catch {
                // silently keep existing data on transient failures
            } finally {
                setTableLoading(false);
            }
        };

        fetchTablePage();
    }, [tablePage]);

    const table = payload?.analytics.table;
    const charts = payload?.analytics.charts ?? [];
    const metrics = payload?.analytics.metrics ?? [];

    return (
        <div className="min-h-screen bg-slate-100 p-4 md:p-6">
            <div className="mb-5">
                <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Police Analytics</h1>
                <p className="mt-1 text-sm text-slate-600">
                    Persona-specific operational dashboards over 112 calls, FIRs, and complaints.
                </p>
            </div>

            <section className="mb-5 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    {/* Persona — left, 2 cols */}
                    <label className="block md:col-span-2">
                        <span className="text-sm font-medium text-slate-700">Persona</span>
                        <select
                            value={persona}
                            onChange={(event) => setPersona(event.target.value as Persona)}
                            className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        >
                            {personas.map((item) => (
                                <option key={item} value={item}>{item}</option>
                            ))}
                        </select>
                    </label>

                    {/* Date range — right, 1 col */}
                    <div ref={datePickerRef} className="relative">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-slate-700">Date Range</span>
                            {selectedRange && (
                                <button
                                    type="button"
                                    onClick={() => { setSelectedRange(null); setDatePickerOpen(false); }}
                                    className="text-xs text-slate-400 hover:text-red-500 transition-colors"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={() => setDatePickerOpen((open) => !open)}
                            className="mt-1 flex w-full items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm text-slate-800 outline-none hover:bg-slate-50 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        >
                            {selectedRange ? (
                                <span>{formatDisplayDate(selectedRange.startDate)} – {formatDisplayDate(selectedRange.endDate)}</span>
                            ) : (
                                <span className="text-slate-400">All time</span>
                            )}
                            <span className="text-xs text-slate-500">{selectedRange ? 'Change' : 'Set range'}</span>
                        </button>
                        {datePickerOpen && (
                            <div className="absolute right-0 z-30 mt-2 flex max-w-[calc(100vw-2rem)] overflow-auto rounded-lg border border-slate-200 bg-white shadow-xl">
                                {/* Quick-select sidebar */}
                                <div className="flex w-36 shrink-0 flex-col gap-1 border-r border-slate-100 p-3">
                                    <span className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Quick select</span>
                                    {quickRanges.map(({ label, range }) => {
                                        const r = range();
                                        const active = selectedRange ? isQuickRangeActive(r, selectedRange) : false;
                                        return (
                                            <button
                                                key={label}
                                                type="button"
                                                onClick={() => setSelectedRange({ ...r, key: 'selection' })}
                                                className={`rounded-md px-3 py-2 text-left text-sm transition-colors ${
                                                    active
                                                        ? 'bg-blue-600 font-medium text-white'
                                                        : 'text-slate-700 hover:bg-slate-100'
                                                }`}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                    <button
                                        type="button"
                                        onClick={() => { setSelectedRange(null); setDatePickerOpen(false); }}
                                        className={`mt-1 rounded-md px-3 py-2 text-left text-sm transition-colors ${
                                            !selectedRange
                                                ? 'bg-blue-600 font-medium text-white'
                                                : 'text-slate-700 hover:bg-slate-100'
                                        }`}
                                    >
                                        All time
                                    </button>
                                </div>
                                {/* Calendar */}
                                <div className="p-2">
                                    <DateRange
                                        editableDateInputs
                                        moveRangeOnFirstSelection={false}
                                        months={2}
                                        direction="horizontal"
                                        maxDate={new Date()}
                                        rangeColors={['#2563eb']}
                                        ranges={[selectedRange ?? getDefaultDateRange()]}
                                        onChange={(ranges) => {
                                            const next = ranges.selection;
                                            if (!next?.startDate || !next?.endDate) return;
                                            setSelectedRange({
                                                startDate: next.startDate,
                                                endDate: next.endDate,
                                                key: 'selection',
                                            });
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="mt-3 text-xs text-slate-500">
                    {scope ? `Scope: ${scope.startDate} to ${scope.endDate}` : 'Scope: All time'}
                </div>
            </section>

            {loading ? (
                <div className="rounded-lg bg-white p-6 text-slate-600 shadow-sm">Loading analytics...</div>
            ) : error ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-red-700">{error}</div>
            ) : !payload ? (
                <div className="rounded-lg bg-white p-6 text-slate-600 shadow-sm">No analytics data available.</div>
            ) : (
                <>
                    <section className="mb-5 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                        {metrics.map((metric) => (
                            <div key={metric.label} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                                <div className="text-sm font-medium text-slate-500">{metric.label}</div>
                                <div className="mt-2 text-3xl font-semibold text-slate-900">{formatMetricValue(metric)}</div>
                                {metric.helper && <div className="mt-1 text-xs text-slate-500">{metric.helper}</div>}
                            </div>
                        ))}
                    </section>

                    <section className="mb-5 grid grid-cols-1 gap-4 xl:grid-cols-2">
                        {charts.map((chart) => (
                            <div key={chart.id} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                                <h2 className="mb-3 text-base font-semibold text-slate-900">{chart.title}</h2>
                                <div className="h-80">
                                    {chart.labels.length > 0 ? (
                                        renderChart(chart)
                                    ) : (
                                        <div className="flex h-full items-center justify-center rounded-md bg-slate-50 text-sm text-slate-500">
                                            No chart data for this scope.
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </section>

                    {table && (
                        <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
                            <div className="border-b border-slate-200 px-4 py-3 flex items-center justify-between">
                                <h2 className="text-base font-semibold text-slate-900">{table.title}</h2>
                                {table.pagination && (
                                    <span className="text-xs text-slate-500">
                                        {table.pagination.totalRows.toLocaleString('en-IN')} total records
                                    </span>
                                )}
                            </div>
                            <div className={`overflow-auto transition-opacity ${tableLoading ? 'opacity-50' : ''}`}>
                                <table className="min-w-full text-sm">
                                    <thead className="bg-slate-50 text-left text-slate-600">
                                        <tr>
                                            {table.columns.map((column) => (
                                                <th key={column} className="whitespace-nowrap px-4 py-3 font-medium">
                                                    {prettifyColumn(column)}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {table.rows.length > 0 ? (
                                            table.rows.map((row, index) => (
                                                <tr key={index} className="hover:bg-slate-50">
                                                    {table.columns.map((column) => (
                                                        <td key={column} className="whitespace-nowrap px-4 py-3 text-slate-700">
                                                            {row[column] === null || row[column] === undefined || row[column] === '' ? '-' : String(row[column])}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td className="px-4 py-4 text-slate-500" colSpan={table.columns.length}>
                                                    No table rows for this scope.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {/* Pagination controls */}
                            {table.pagination && table.pagination.totalPages > 0 && (
                                <div className="flex flex-col gap-3 border-t border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="flex items-center gap-2 text-sm text-slate-600">
                                        <span>Rows per page</span>
                                        <select
                                            value={tablePageSize}
                                            onChange={(e) => {
                                                setTablePageSize(Number(e.target.value));
                                                setTablePage(1);
                                            }}
                                            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                                        >
                                            {[10, 25, 50, 100].map((size) => (
                                                <option key={size} value={size}>{size}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button
                                            type="button"
                                            disabled={table.pagination.page <= 1 || tableLoading}
                                            onClick={() => setTablePage(1)}
                                            className="rounded-md px-2 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 transition-colors"
                                            title="First page"
                                        >
                                            ⟨⟨
                                        </button>
                                        <button
                                            type="button"
                                            disabled={table.pagination.page <= 1 || tableLoading}
                                            onClick={() => setTablePage((p) => Math.max(1, p - 1))}
                                            className="rounded-md px-2.5 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 transition-colors"
                                        >
                                            ‹ Prev
                                        </button>
                                        {(() => {
                                            const { page, totalPages } = table.pagination;
                                            const pages: (number | '...')[] = [];
                                            if (totalPages <= 7) {
                                                for (let i = 1; i <= totalPages; i++) pages.push(i);
                                            } else {
                                                pages.push(1);
                                                if (page > 3) pages.push('...');
                                                for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
                                                if (page < totalPages - 2) pages.push('...');
                                                pages.push(totalPages);
                                            }
                                            return pages.map((p, idx) =>
                                                p === '...' ? (
                                                    <span key={`ellipsis-${idx}`} className="px-1.5 py-1 text-sm text-slate-400">…</span>
                                                ) : (
                                                    <button
                                                        key={p}
                                                        type="button"
                                                        disabled={tableLoading}
                                                        onClick={() => setTablePage(p)}
                                                        className={`min-w-[2rem] rounded-md px-2 py-1.5 text-sm font-medium transition-colors ${
                                                            p === page
                                                                ? 'bg-blue-600 text-white shadow-sm'
                                                                : 'text-slate-600 hover:bg-slate-100'
                                                        }`}
                                                    >
                                                        {p}
                                                    </button>
                                                )
                                            );
                                        })()}
                                        <button
                                            type="button"
                                            disabled={table.pagination.page >= table.pagination.totalPages || tableLoading}
                                            onClick={() => setTablePage((p) => Math.min(table.pagination.totalPages, p + 1))}
                                            className="rounded-md px-2.5 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 transition-colors"
                                        >
                                            Next ›
                                        </button>
                                        <button
                                            type="button"
                                            disabled={table.pagination.page >= table.pagination.totalPages || tableLoading}
                                            onClick={() => setTablePage(table.pagination.totalPages)}
                                            className="rounded-md px-2 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 transition-colors"
                                            title="Last page"
                                        >
                                            ⟩⟩
                                        </button>
                                    </div>
                                    <div className="text-sm text-slate-500">
                                        Page {table.pagination.page} of {table.pagination.totalPages}
                                    </div>
                                </div>
                            )}
                        </section>
                    )}
                </>
            )}
        </div>
    );
}
